import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { Plan } from '@zapier/design-system-revenue/src/types';
import { useCurrentAccountId } from '@zapier/design-system-revenue/src/hooks';
import { useCurrentPlan } from '@zapier/design-system-revenue/src/utils';

const nextPlanQuery = gql`
  query nextPlanQuery($accountId: Int!, $currentPlanId: Int!) {
    nextPlan(accountId: $accountId, currentPlanId: $currentPlanId) {
      amount
      billingInterval
      hasMeteredBilling
      id
      intervals {
        id
        amount
        interval
        monthlyAmount
      }
      name
      tiers {
        numTasks
        pricePerTask
      }
      limits {
        polling_interval
        zap_limit
        zap_length
        action_limit
        throttle_multiplier
        member_limit
        premium_apps_limit
      }
    }
  }
`;

type UseNextPlanOutput = {
  isLoading: boolean;
  nextPlan: Plan;
  error: string | undefined;
};

export const useNextPlan = (): UseNextPlanOutput => {
  const { accountId } = useCurrentAccountId();
  const { currentPlan, isLoading: isLoadingCurrentPlan } = useCurrentPlan();

  const { loading: isLoadingNextPlan, data, error } = useQuery(nextPlanQuery, {
    skip: !accountId || isLoadingCurrentPlan,
    variables: {
      accountId,
      /**
       * I don't know why, but currentPlan.id is coming back as a string, not a number, even
       * thought the TypeScript definition says it's a number. If the ts-ignore is removed,
       * TypeScript complains that parseInt is expecting a string param and receiving a number
       * param.
       */
      // @ts-ignore
      currentPlanId: parseInt(currentPlan?.id ?? '-1'),
    },
  });

  return {
    isLoading: isLoadingCurrentPlan || isLoadingNextPlan,
    nextPlan: data?.nextPlan,
    error: error ? 'Error loading next plan information.' : undefined,
  };
};
